import * as THREE from "three";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { PhysicsCube } from "./physics_cube.js";
import { PhysicsTable } from "./physics_table.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { PhysicsMass } from "../physics/physics_masses.js";
import { hapticsController, VibrationContext } from "../haptics/haptics_controller.js";
import { HapticPattern } from "../haptics/haptics_constants.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { EntityKind, LevelEntity } from "../types/level_entity.js";
import { BreakablePhysicsEntity } from "./breakable_physics_entity.js";
import { edgeSupportFor } from "../physics/edge_support_pad.js";
import { BlockImpactMaterial } from "../vfx_3d/effects/ball_impact/vfx_ball_block_impact_task.js";

export class TreeBlock extends BreakablePhysicsEntity implements LevelEntity
{
  public readonly kind = EntityKind.Tree;

  private static readonly MODELS: Record<number, ResourceID> = {
    1: ResourceID.TreeBlock1x1Model,
    2: ResourceID.TreeBlock1x2Model,
    3: ResourceID.TreeBlock1x3Model,
    4: ResourceID.TreeBlock1x4Model,
    5: ResourceID.TreeBlock1x5Model,
  };

  private static material: THREE.MeshLambertMaterial | null = null;
  private static readonly FADE_DURATION = 0.35;
  private static readonly OUTSIDE_TIMER_DURATION = 3.0;
  private static readonly GROUND_DESTROY_THRESHOLD_Y = 0.2;
  private static readonly FALL_DESTROY_THRESHOLD_Y = -2.0;
  private static readonly LINEAR_DAMPING = 0.04;
  private static readonly ANGULAR_DAMPING = 0.35;
  // Held at the values this class has always used, on purpose. 0.45 is the GLASS
  // grip (PhysicsFriction.glass) on a block made of wood — plywood's 0.7 would be
  // the honest number — but grip and bounce are what the balance curve's shot
  // counts are measured against, so changing them re-balances every level that
  // carries a log. Kept as-is to leave the collider fix below purely geometric.
  private static readonly FRICTION = 0.45;
  private static readonly RESTITUTION = 0.08;

  // A block occupies exactly one of these two axes (1-3), never both — the
  // model only exists as a vertical trunk, so a "wide" piece (a fallen log)
  // is the same model/texture rotated -90° onto its side (see createMesh).
  // Matches PhysicsCube.createAuthoredMesh's `width > height` convention.
  public readonly width_blocks: number;
  public readonly height_blocks: number;

  private outside_pending = false;
  private outside_timer = 0;
  private fade_timer = 0;

  constructor(
    position: THREE.Vector3,
    width_blocks = 1,
    height_blocks = 1,
    lying = false,
    rotation?: { x?: number; y?: number; z?: number },
    surface_tilt_rad = 0
  )
  {
    const blocks = THREE.MathUtils.clamp( Math.round( Math.max( width_blocks, height_blocks ) ), 1, 5 );
    const horizontal = width_blocks > height_blocks;

    const mesh = TreeBlock.createMesh( blocks, horizontal, lying );

    // Body/collider come from BreakablePhysicsEntity, same as every other block, so
    // the collider is the mesh's own bounding box and the body gets the stack's extra
    // solver iterations. This class used to build both by hand and shrink the box by
    // a 5 mm padding on all six sides — and since createLayeredBlocks stacks rows
    // exactly PhysicsCube.getSize().y apart, that padding left every log hanging 5 mm
    // above whatever it stood on. At level start every log in the tower dropped and
    // kicked the stack, so a level that looked perfectly stacked in the editor fell
    // over on its own. Measuring the mesh also keeps `lying` honest: the flag rotates
    // the mesh (see createMesh) and a hand-built box never followed it.
    super( mesh, position, {
      angular_damping: TreeBlock.ANGULAR_DAMPING,
      arm_delay: 0,
      friction: TreeBlock.FRICTION,
      linear_damping: TreeBlock.LINEAR_DAMPING,
      mass: PhysicsMass.tree_block_per_cell * blocks,
      restitution: TreeBlock.RESTITUTION,
      initial_rotation_x: rotation?.x,
      initial_rotation_y: rotation?.y,
      initial_rotation_z: rotation?.z,
      extra_colliders: edgeSupportFor( mesh, rotation, surface_tilt_rad ),
    } );

    this.width_blocks = horizontal ? blocks : 1;
    this.height_blocks = horizontal ? 1 : blocks;
    this.mesh.position.copy( position );
  }

  private static createMesh( blocks: number, horizontal: boolean, lying: boolean ): THREE.Object3D
  {
    const model_id = TreeBlock.MODELS[blocks];
    const visual = ResourceManager.getModel( model_id );
    const material = TreeBlock.getMaterial();

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) ) return;
      child.castShadow = true;
      child.receiveShadow = false;
      child.material = material;
    } );

    // The model is authored standing up along local Y — lay it on its side
    // for a horizontal (width > height) piece, same convention as
    // PhysicsCube.createAuthoredMesh's authored long models.
    if ( horizontal )
      visual.rotation.z = -Math.PI * 0.5;

    visual.updateMatrixWorld( true );
    const base_size = new THREE.Box3().setFromObject( visual ).getSize( new THREE.Vector3() );
    if ( base_size.x <= 0 || base_size.y <= 0 || base_size.z <= 0 )
      throw new Error( `Tree block model ${model_id} has invalid dimensions` );

    const target_size = PhysicsCube.getSize();
    if ( horizontal )
    {
      visual.scale.set(
        target_size.x * blocks / base_size.x,
        target_size.y / base_size.y,
        target_size.z / base_size.z
      );
    }
    else
    {
      visual.scale.set(
        target_size.x / base_size.x,
        target_size.y * blocks / base_size.y,
        target_size.z / base_size.z
      );
    }

    visual.updateMatrixWorld( true );
    const center = new THREE.Box3().setFromObject( visual ).getCenter( new THREE.Vector3() );
    visual.position.sub( center );

    const group = new THREE.Group();
    group.add( visual );

    if ( lying ) group.rotation.x = Math.PI / 2;

    const root = new THREE.Group();
    root.add( group );
    return root;
  }

  private static getMaterial(): THREE.MeshLambertMaterial
  {
    if ( !this.material )
    {
      this.material = new THREE.MeshLambertMaterial( {
        map: ResourceManager.getTexture( ResourceID.TreeTexture ),
        normalMap: ResourceManager.getNormalMap( ResourceID.TreeNormalMap ),
      } );
    }
    return this.material;
  }

  public checkDestroy( dt: number, table: PhysicsTable ): void
  {
    if ( this.destroying ) return;
    const pos = this.body.translation();
    const is_outside = table.isOutside( pos );
    const bottom_y = pos.y - this.halfHeight();

    if ( !is_outside )
    {
      this.outside_pending = false;
      return;
    }

    if ( bottom_y <= TreeBlock.GROUND_DESTROY_THRESHOLD_Y || pos.y < TreeBlock.FALL_DESTROY_THRESHOLD_Y )
    {
      this.destroy();
      return;
    }

    if ( !this.outside_pending && table.isBelowSurface( pos ) )
    {
      this.outside_pending = true;
      this.outside_timer = TreeBlock.OUTSIDE_TIMER_DURATION;
    }

    // Unconditional after the timer: a block parked above the ground (on the
    // table base or wedged on the leg) would otherwise stay outside_pending
    // forever, and isAtRest() === false would block the game-end check.
    if ( this.outside_pending && ( this.outside_timer -= dt ) <= 0 )
    {
      this.destroy();
    }
  }

  public isAtRest(): boolean
  {
    return !this.outside_pending && super.isAtRest();
  }

  public canBeHit(): boolean
  {
    return !this.destroying;
  }

  public onBallHit( point: THREE.Vector3 ): void
  {
    this.playHitSound();
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
    Vfx3DManager.addCannonBallBlockImpact( point, BlockImpactMaterial.WOOD );
  }

  public onFireworkHit( point: THREE.Vector3 ): void
  {
    this.destroy();
  }

  public checkSelfDestruct( dt: number, table: PhysicsTable ): void
  {
    this.checkDestroy( dt, table );
  }

  public playHitSound(): void
  {
    void audioController.playSound( AudioClips.WOOD_CUBE_HIT );
  }

  private setOpacity( opacity: number ): void
  {
    this.mesh.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) ) return;
      if ( !child.userData.materialCloned )
      {
        child.material = Array.isArray( child.material )
          ? child.material.map( material => material.clone() )
          : child.material.clone();
        child.userData.materialCloned = true;
      }
      const materials = Array.isArray( child.material ) ? child.material : [ child.material ];
      for ( const material of materials )
      {
        material.transparent = true;
        material.opacity = opacity;
        material.depthWrite = false;
      }
      child.castShadow = false;
    } );
  }

  protected updateDestroying( dt: number ): void
  {
    this.fade_timer -= dt;
    this.setOpacity( Math.max( 0, this.fade_timer / TreeBlock.FADE_DURATION ) );
    if ( this.fade_timer <= 0 ) this.markDead();
  }

  public destroy(): void
  {
    if ( !this.beginDestroying() ) return;
    this.fade_timer = TreeBlock.FADE_DURATION;
  }
}
